/**
 * ArRowActions Component
 *
 * Renders a dropdown menu with row-level actions for ArTable.
 * Actions: Add/Remove Annotation, Lock/Unlock, Copy Rows, Paste Rows,
 * Duplicate, Insert, Delete. Reads row state and actions directly from context.
 *
 * Delete asks nothing and runs straight away: `useRowDeleteTargets` routes it
 * through the callable, whose trash the undo buttons reverse by id (SYD-2447).
 * The Modal here belongs to Remove Annotation, which has no way back.
 *
 * Copy Rows and Paste Rows come off `ArTableClipboardContext` (SYD-2314) — the
 * single instance `ArTableProvider` builds, which the chord, the context menu
 * and the bulk menu also drive — anchored on THIS row. Reading the context
 * rather than calling the hook is what keeps an N-row page at one clipboard
 * instance instead of 2N.
 */

import { FC, memo, useState, CSSProperties, useContext } from 'react';
import { Button, Dropdown, Modal, theme, Tooltip, Typography } from 'antd';
import {
  MoreOutlined,
  DeleteOutlined,
  CommentOutlined,
  CloseOutlined,
  LockOutlined,
  UnlockOutlined,
  CopyOutlined,
  PlusOutlined,
  DisconnectOutlined,
  SnippetsOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { ArTableActionsContext, ArTableBundleContext, ArTableClipboardContext } from '../context';
import { useRowDeleteTargets } from './useRowDeleteTargets';
import type { ArBundleType, ArRowId } from '#types/System.Artifacts.types';

const { Text } = Typography;

type ArRowActionsProps = {
  rowId: ArRowId;
  isLocked: boolean;
  annotation: string | undefined;
  disabled?: boolean;
  style?: CSSProperties;
  bundleRole?: 'member';
  bundleId?: string;
  bundleType?: ArBundleType;
};

type ModalState = 'remove-annotation' | null;

/**
 * ArRowActions renders a dropdown with row operations.
 */
const ArRowActions: FC<ArRowActionsProps> = ({
  rowId,
  isLocked,
  annotation,
  disabled = false,
  style,
  bundleRole,
  bundleId,
  bundleType,
}) => {
  const { token } = theme.useToken();
  const actions = useContext(ArTableActionsContext)!;
  const bundleCtx = useContext(ArTableBundleContext);
  const { copyRowsToClipboard, pasteRowsFromClipboard } = useContext(ArTableClipboardContext)!;
  const [modalState, setModalState] = useState<ModalState>(null);
  const [adding, setAdding] = useState(false);
  const deleteTargets = useRowDeleteTargets(rowId, isLocked);

  const {
    backgroundColor: passedBackgroundColor,
    background: _passedBackground,
    ...restStyle
  } = style ?? {};

  const rowBackgroundColor =
    typeof passedBackgroundColor === 'string' ? passedBackgroundColor : token.colorBgBase;

  const stickyBackgroundColor = `color-mix(in srgb, ${rowBackgroundColor} 56%, transparent)`;

  const hasAnnotation = annotation !== undefined;

  const handleModalOk = () => {
    if (modalState === 'remove-annotation') {
      actions.updateAnnotation(rowId, undefined);
    }
    setModalState(null);
  };

  const handleModalCancel = () => {
    setModalState(null);
  };

  const handleAddAnnotation = () => {
    actions.updateAnnotation(rowId, '');
  };

  const handleAddRowAfter = async () => {
    setAdding(true);
    try {
      if (bundleRole === 'member' && bundleId) {
        await actions.addMemberRow(bundleId, rowId);
      } else {
        await actions.addRow(undefined, { afterRowId: rowId });
      }
    } finally {
      setAdding(false);
    }
  };

  const handleAddRowAsUngrouped = async () => {
    setAdding(true);
    try {
      await actions.addRow(undefined, { afterRowId: rowId });
    } finally {
      setAdding(false);
    }
  };

  const bundleTypeLabel = bundleType === 'kit' ? 'Kit' : 'Group';

  const moveToBundleChildren: NonNullable<MenuProps['items']> = bundleCtx
    ? Array.from(bundleCtx.bundleMap.entries())
      .filter(([id]) => id !== bundleId)
      .map(([id, entry]) => ({
        key: `move-to-${id}`,
        label: `${entry.bundleType === 'kit' ? 'Kit' : 'Group'}: ${entry.label}`,
        onClick: () => actions.moveToBundle(rowId, id),
        disabled,
      }))
    : [];

  const bundleItems: NonNullable<MenuProps['items']> = bundleCtx && bundleRole === 'member'
    ? [
      { type: 'divider' as const },
      {
        key: 'add-member',
        label: 'Add Member Below',
        icon: <PlusOutlined />,
        onClick: handleAddRowAfter,
        disabled: disabled || adding,
      },
      {
        key: 'remove-from-bundle',
        label: `Remove from ${bundleTypeLabel}`,
        icon: <DisconnectOutlined />,
        onClick: () => actions.removeFromBundle(rowId),
        disabled,
      },
      ...(moveToBundleChildren.length > 0 ? [{
        key: 'move-to-bundle',
        label: 'Move to...',
        children: moveToBundleChildren,
        disabled,
      }] : []),
    ]
    : [];

  const items: MenuProps['items'] = [
    !hasAnnotation && {
      key: 'add-annotation',
      label: 'Add Annotation',
      icon: <CommentOutlined />,
      onClick: handleAddAnnotation,
      disabled: disabled || isLocked,
    },
    hasAnnotation && {
      key: 'remove-annotation',
      label: 'Remove Annotation',
      icon: <CloseOutlined />,
      onClick: () => setModalState('remove-annotation'),
      disabled: disabled || isLocked,
    },
    { type: 'divider' as const },
    !isLocked && {
      key: 'lock',
      label: 'Lock Row',
      icon: <LockOutlined />,
      onClick: () => actions.lockRow(rowId, true),
      disabled: disabled,
    },
    isLocked && {
      key: 'unlock',
      label: 'Unlock Row',
      icon: <UnlockOutlined />,
      onClick: () => actions.lockRow(rowId, false),
      disabled: disabled,
    },
    ...bundleItems,
    { type: 'divider' as const },
    {
      key: 'copy-rows',
      label: 'Copy Rows',
      icon: <CopyOutlined />,
      onClick: () => void copyRowsToClipboard(rowId),
      disabled: disabled,
    },
    {
      key: 'paste-rows',
      label: 'Paste Rows',
      icon: <SnippetsOutlined />,
      onClick: () => void pasteRowsFromClipboard(rowId),
      disabled: disabled,
    },
    { type: 'divider' as const },
    {
      key: 'duplicate',
      label: 'Duplicate Row',
      icon: <CopyOutlined />,
      onClick: () => actions.duplicateRow(rowId),
      disabled: disabled,
    },
    bundleCtx && bundleRole === 'member'
      ? {
        key: 'add-row',
        label: 'Insert Row Below',
        icon: <PlusOutlined />,
        disabled: disabled || adding,
        children: [
          {
            key: 'add-member-below',
            label: 'As Member',
            onClick: handleAddRowAfter,
            disabled: disabled || adding,
          },
          {
            key: 'add-ungrouped-below',
            label: 'As Ungrouped',
            onClick: handleAddRowAsUngrouped,
            disabled: disabled || adding,
          },
        ],
      }
      : {
        key: 'add-row',
        label: 'Insert Row Below',
        icon: <PlusOutlined />,
        onClick: handleAddRowAfter,
        disabled: disabled || adding,
      },
    {
      key: 'delete',
      /**
       * A disabled action that says why. An antd Tooltip, not the native `title`
       * attribute: the browser's own tooltip renders outside the document, so
       * neither a test nor a verification pass can see whether the reason is there
       * at all — and one that cannot be observed was, in fact, not being shown.
       * The wrapper carries no `aria-label`: it would become the menu item's own
       * accessible name and rename the action.
       */
      label: deleteTargets.disabledReason === null
        ? deleteTargets.label
        : (
          <Tooltip title={deleteTargets.disabledReason} placement="left">
            <Text>{deleteTargets.label}</Text>
          </Tooltip>
        ),
      icon: <DeleteOutlined />,
      onClick: () => { void deleteTargets.deleteRows(); },
      disabled: disabled || deleteTargets.disabled,
      danger: true,
    },
  ].filter(Boolean) as MenuProps['items'];

  return (
    <div 
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: isLocked ? 'not-allowed' : undefined,
        width: 40,
        minWidth: 40,
        maxWidth: 40,
        gridColumnEnd: -1,
        borderLeft: `2px solid ${token.colorBorderSecondary}60`,
        position: 'sticky',
        right: 0,
        zIndex: 20,
        backdropFilter: 'blur(3px)',
        backgroundColor: stickyBackgroundColor,
        ...restStyle,
      }} 
      role="cell"
    >
      <Dropdown
        menu={{ items }}
        trigger={['click']}
        disabled={disabled}
        onOpenChange={deleteTargets.capture}
      >
        <Button
          type="text"
          icon={<MoreOutlined />}
          aria-label="Row actions"
          size="small"
          style={{ padding: 4 }}
        />
      </Dropdown>

      <Modal
        title="Remove Annotation"
        open={modalState !== null}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText="Remove"
        cancelText="Cancel"
        aria-label="Remove annotation confirmation"
      >
        <Text>
          Are you sure you want to remove the annotation from this row?
        </Text>
      </Modal>
    </div>
  );
};

export default memo(ArRowActions);

